/**
 * D-37: the per-head net beside 6.1 — for tying ProGST to a working paper.
 *
 * The practice's GSTR-3B workings net each head on its own; Rule 88A lets
 * credit in one head pay another, and the portal applies 88A at filing. So 6.1
 * and the carry-forward stay on 88A, and this view exists only so a
 * practitioner holding their working can see where and by how much the two
 * differ — rather than hunting a "bug" in figures that are both right for what
 * they are.
 */

const HEAD_ORDER = ['igst', 'cgst', 'sgst', 'cess'] as const
type Head = (typeof HEAD_ORDER)[number]

export type PerHeadNet = {
  cash: Record<Head, number>
  creditRemaining: Record<Head, number>
  /** Heads where the working would show MORE cash than 88A — the difference. */
  differences: { head: Head; paise: number }[]
  note: string
}

function heads(value: unknown): Record<Head, number> {
  const source = (value ?? {}) as Record<string, unknown>
  return Object.fromEntries(
    HEAD_ORDER.map((h) => [h, typeof source[h] === 'number' ? (source[h] as number) : 0]),
  ) as Record<Head, number>
}

/** Paise to whole rupees (still paise), HALF_UP away from zero — the service's V-09 rule. */
export function wholeRupees(paise: number): number {
  return Math.sign(paise) * Math.round(Math.abs(paise) / 100) * 100
}

/**
 * The per-head net as the 3B screen shows it beside a whole-rupee 6.1 (C-09, FR-46): each
 * figure in whole rupees, and the difference worked again against 6.1's whole-rupee cash, so
 * the two tables side by side never disagree by paise the return does not carry.
 * **Screen only** — the service computes the view in paise, and the working paper prints it so.
 */
export function perHeadNetWhole(
  view: PerHeadNet | null,
  filedCash: Record<string, number> | undefined,
): PerHeadNet | null {
  if (!view) return null
  const round = (r: Record<Head, number>) =>
    Object.fromEntries(HEAD_ORDER.map((h) => [h, wholeRupees(r[h])])) as Record<Head, number>
  const cash = round(view.cash)
  return {
    cash,
    creditRemaining: round(view.creditRemaining),
    differences: HEAD_ORDER.flatMap((head) => {
      const more = cash[head] - (filedCash?.[head] ?? 0)
      return more !== 0 ? [{ head, paise: more }] : []
    }),
    note: view.note,
  }
}

/** The view from a 6.1 cell, or null when the service did not send one. */
export function perHeadNet(cell: Record<string, unknown> | undefined): PerHeadNet | null {
  const view = cell?.per_head_net as Record<string, unknown> | undefined
  if (!view || typeof view !== 'object') return null
  const more = heads(view.cash_more_than_88a)
  return {
    cash: heads(view.cash),
    creditRemaining: heads(view.credit_remaining),
    differences: HEAD_ORDER.flatMap((head) =>
      more[head] !== 0 ? [{ head, paise: more[head] }] : [],
    ),
    note: typeof view.note === 'string' ? view.note : '',
  }
}
